import { useMemo } from "react";
import { AppLink, DataTable, dataColumns, formatDateTime, formatRelative, useNarrow, type DataColumn } from "@amber/ui";
import { platformEntry, type GapRow, type SourcesResponse } from "../../contracts/index";
import { GapMenu, gapInterval } from "./GapActions";
import { Monogram, PlatformCode } from "./SourceVisuals";

const col = dataColumns<GapRow>();

const SOURCE = col.display({
  id: "source",
  header: "Source",
  meta: { width: 220 },
  cell: ({ row: { original: g } }) => <b className="truncate font-strong text-fg">{g.source.title}</b>,
});

const OWNER: readonly DataColumn<GapRow>[] = [
  col.display({
    id: "people",
    header: "Person(s)",
    meta: { width: 120 },
    cell: ({ row: { original: g } }) => (
      <span className="flex">
        {g.people.length > 0 ? g.people.map((p) => <Monogram key={p.id} name={p.name} />) : "—"}
      </span>
    ),
  }),
  col.display({
    id: "platform",
    header: "Platform",
    meta: { width: 120 },
    cell: ({ row: { original: g } }) => (
      <span className="inline-flex items-center gap-1.5">
        <PlatformCode code={platformEntry(g.platform.key)?.code ?? g.platform.key} />
        {g.platform.label}
      </span>
    ),
  }),
];

const GAP: readonly DataColumn<GapRow>[] = [
  col.display({
    id: "interval",
    header: "Missing interval",
    meta: { width: 190 },
    cell: ({ row: { original: g } }) => (
      <span className="inline-flex items-center gap-1.5 text-fg">
        <span
          aria-hidden="true"
          className="inline-block h-2.5 w-4 rounded-[2px] border border-attention bg-[repeating-linear-gradient(135deg,var(--color-attention)_0_2px,var(--color-attention-soft)_2px_5px)]"
        />
        {gapInterval(g)}
      </span>
    ),
  }),
  col.display({
    id: "reason",
    header: "Reason",
    meta: { width: 300 },
    cell: ({ row: { original: g } }) => <span title={g.reason}>{g.reason}</span>,
  }),
];

const HOW = col.display({
  id: "how",
  header: "How",
  meta: { width: 300 },
  cell: ({ row: { original: g } }) => (
    <span title={g.resolved?.note ?? undefined}>
      {g.resolved ? `${g.resolved.how} · ${formatDateTime(g.resolved.at)} · ${g.resolved.by}` : ""}
    </span>
  ),
});

const OPEN: readonly DataColumn<GapRow>[] = [
  col.display({
    id: "since",
    header: "Since",
    meta: { width: 90 },
    cell: ({ row: { original: g } }) => formatRelative(g.opened_at),
  }),
  col.display({
    id: "next",
    header: "Next step",
    meta: { width: 200 },
    cell: ({ row: { original: g } }) =>
      g.handling === "needs_person" && g.task_id ? (
        <AppLink href={`/tasks/${g.task_id}`} className="text-link">
          {g.next_step}
        </AppLink>
      ) : (
        g.next_step
      ),
  }),
];

function gapColumns(
  narrow: boolean,
  resolved: boolean,
  actions: SourcesResponse["actions"],
): readonly DataColumn<GapRow>[] {
  const menu = col.display({
    id: "actions",
    header: () => <span className="sr-only">Gap actions</span>,
    meta: { width: 48, label: "Gap actions", interactive: true, cellClassName: "overflow-visible" },
    cell: ({ row }) => <GapMenu gap={row.original} actions={actions} compact />,
  });
  if (narrow) return resolved ? [SOURCE, ...GAP] : [SOURCE, ...GAP, menu];
  return resolved ? [SOURCE, ...OWNER, ...GAP, HOW] : [SOURCE, ...OWNER, ...GAP, ...OPEN, menu];
}

const PINNED = ["source"];

export function GapsTable({
  gaps,
  resolved,
  selectedId = null,
  onSource,
  actions,
}: {
  gaps: readonly GapRow[];
  resolved: boolean;
  selectedId?: string | null;
  onSource: (id: string) => void;
  actions: SourcesResponse["actions"];
}) {
  const narrow = useNarrow();
  const columns = useMemo(() => gapColumns(narrow, resolved, actions), [narrow, resolved, actions]);
  return (
    <DataTable
      label={resolved ? "Resolved gaps" : "Open gaps"}
      columns={columns}
      rows={gaps}
      rowId={(g) => g.id}
      minWidth={narrow ? 560 : 1100}
      pinned={PINNED}
      selectedId={selectedId}
      empty={resolved ? "No gaps were resolved in the last 30 days." : "No open gaps."}
      rowOptions={(g) => ({
        label: `${g.source.title}: ${g.reason}`,
        onOpen: () => {
          onSource(g.source.id);
        },
      })}
    />
  );
}
